// Single destination for every PUBLIC lead form on the Signal website:
// the Hair Art dashboard CRM bridge.
//
// Security notes:
// - No secrets here. The endpoint accepts anonymous browser POSTs from
//   hairartclinics.net / www.hairartclinics.net and enforces its own
//   auth + rate limits server-side.
// - Public forms must NOT write to Supabase directly.

import { createClientUuid } from "@/lib/clientUuid";
import { getQueryAttribution } from "@/lib/brandQuery";
import { supabase } from "@/integrations/supabase/client";
import { trackPersistedLead } from "@/lib/gtm";

export const CRM_BRIDGE_ENDPOINT =
  "https://hair-art-clinic-dashboard.vercel.app/api/website/forms/lead";

export const CRM_BRIDGE_VERSION = "all-forms-2026-08-17";

export type WebsiteFormType =
  | "consultation"
  | "contact"
  | "callback"
  | "hair-analysis"
  | "candidacy"
  | "pdf-download"
  | "landing-page"
  | "newsletter"
  | "chat";

const SUBJECTS: Record<WebsiteFormType, string> = {
  consultation: "Website consultation request",
  contact: "Website contact request",
  callback: "Website call-back request",
  "hair-analysis": "Website hair analysis request",
  candidacy: "Website suitability quiz request",
  "pdf-download": "Website PDF download request",
  "landing-page": "Website landing page request",
  newsletter: "Website newsletter signup",
  chat: "Website chat request",
};

export type WebsiteLeadInput = {
  formType: WebsiteFormType;
  name?: string;
  phone?: string;
  email?: string;
  language?: string;
  marketingConsent?: boolean;
  turnstile_token?: string;
  /** Hidden honeypot value — real users leave this empty. */
  website?: string;
  /** Extra readable details summarized into `message`. */
  details?: Record<string, unknown>;
  /** Optional free-text note from the user. */
  message?: string;
  /** Optional explicit source override (kept under 80 chars). */
  source?: string;
};

export interface WebsiteLeadResult {
  ok: boolean;
  status?: number;
  duplicate?: boolean;
  data?: unknown;
  error?: string;
  apiMessage?: string;
}

const LANGUAGE_CODES = ["TR", "EN", "NL", "FR", "DE", "AR", "ES", "IT"];

/** Contact must be usable, not merely a nonempty placeholder. */
export function hasChatContact(input: { phone?: string; email?: string }): boolean {
  const phone = input.phone?.trim() ?? "";
  const email = input.email?.trim() ?? "";
  return (/^[+\d\s().-]+$/.test(phone) && /^\d{7,15}$/.test(phone.replace(/\D/g, ""))) ||
    (email.length <= 255 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
}

/** Derive an uppercase language code from the route first, then i18n state. */
export function resolveLanguageCode(language?: string): string {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const routeMatch = path.match(/^\/([a-z]{2})(\/|$)/i);
  const fromRoute = routeMatch ? routeMatch[1].toUpperCase() : "";
  if (LANGUAGE_CODES.includes(fromRoute)) return fromRoute;
  const fromState = (language || "").trim().slice(0, 2).toUpperCase();
  if (LANGUAGE_CODES.includes(fromState)) return fromState;
  if (path.startsWith("/") && path.length > 1 && !routeMatch) return "EN";
  return fromState || "Unknown";
}

/** `hairartclinics.net/tr/book-consultation`, capped at 80 chars. */
export function buildLeadSource(explicit?: string): string {
  if (explicit) return explicit.slice(0, 80);
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const clean = path.replace(/\/+$/, "") || "/";
  return `hairartclinics.net${clean === "/" ? "/" : clean}`.slice(0, 80);
}

function summarize(input: WebsiteLeadInput, source: string): string {
  const lines: string[] = [
    `Form: ${SUBJECTS[input.formType]}`,
    `Route: ${source}`,
    `Phone provided: ${input.phone ? "yes" : "no"}`,
    `Email provided: ${input.email ? "yes" : "no"}`,
  ];

  if (input.message) lines.push(`Message: ${input.message}`);

  for (const [key, value] of Object.entries(input.details || {})) {
    if (value === undefined || value === null || value === "") continue;
    const label = key.replace(/_/g, " ");
    const printable = Array.isArray(value)
      ? value.filter(Boolean).join(", ")
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
    if (!printable) continue;
    lines.push(`${label}: ${printable}`.slice(0, 300));
  }

  return lines.join("\n").slice(0, 4000);
}

async function readBody(response: Response): Promise<{ data: unknown; apiMessage: string }> {
  const raw = await response.text().catch(() => "");
  if (!raw) return { data: null, apiMessage: "" };
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const candidate = parsed?.message ?? parsed?.error ?? parsed?.detail;
    return { data: parsed, apiMessage: typeof candidate === "string" ? candidate : "" };
  } catch {
    return { data: raw, apiMessage: raw.slice(0, 200) };
  }
}

/**
 * POST a public website lead to the CRM bridge. Used by EVERY public form.
 */
export async function submitWebsiteLeadForm(input: WebsiteLeadInput): Promise<WebsiteLeadResult> {
  // Match notify-new-lead validation before creating anything in the CRM.
  if (input.formType === "chat" && !hasChatContact(input)) {
    return { ok: false, status: 400, error: "validation_error" };
  }
  if (['callback', 'consultation', 'contact', 'landing-page'].includes(input.formType)) {
    if (!input.turnstile_token?.trim()) return { ok: false, status: 400, error: 'captcha_required' };
    try {
      const verified = await supabase.functions.invoke('verify-lead-captcha', { body: { token: input.turnstile_token } });
      if (verified.error || verified.data?.ok !== true) return { ok: false, status: 403, error: 'captcha_failed' };
    } catch { return { ok: false, status: 403, error: 'captcha_failed' }; }
  }
  const source = buildLeadSource(input.source);
  const attribution = getQueryAttribution();
  const payload = {
    // Brand-query attribution travels with the lead so the CRM can report
    // conversion rate per brand / non-brand query and per Ads click id.
    attribution: {
      queryType: attribution.query_type,
      channelGroup: attribution.channel_group,
      searchTerm: attribution.search_term,
      matchedBrandTerm: attribution.matched_brand_term,
      campaign: attribution.campaign,
      utmSource: attribution.source,
      utmMedium: attribution.medium,
      gclid: attribution.gclid,
    },
    id: createClientUuid(),
    name: (input.name || "").trim(),
    phone: (input.phone || "").trim(),
    email: (input.email || "").trim().toLowerCase(),
    language: resolveLanguageCode(input.language),
    subject: SUBJECTS[input.formType],
    message: summarize(input, source),
    marketingConsent: !!input.marketingConsent,
    source,
    website: input.website || "",
  };

  console.info("[crm-bridge] submit", { formType: input.formType, source, version: CRM_BRIDGE_VERSION });

  try {
    const response = await fetch(CRM_BRIDGE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, apiMessage } = await readBody(response);

    const reportedDuplicate = !!data && typeof data === "object" && (data as { duplicate?: boolean }).duplicate === true;
    if (!response.ok && !(response.status === 409 && reportedDuplicate)) {
      console.warn("[crm-bridge] submit failed", { status: response.status });
      return { ok: false, status: response.status, error: `crm_bridge_${response.status}`, apiMessage };
    }

    const duplicate =
      response.status === 409 ||
      reportedDuplicate;

    if (data && typeof data === "object" && (data as { ok?: boolean }).ok === false) {
      return { ok: false, status: 502, error: "upstream_error" };
    }
    const stored = await notifyClinicOfNewLead(input, payload.language);
    if (!stored.ok) return stored;
    const saved = stored.data as { id?: string } | undefined;
    if (saved?.id) trackPersistedLead(saved.id, duplicate || !!stored.duplicate, input.formType);
    return { ok: true, status: response.status, duplicate: duplicate || stored.duplicate, data: stored.data };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn("[crm-bridge] submit exception", message);
    return { ok: false, error: "network_error", apiMessage: message };
  }
}

const HEALTH_FORMS: WebsiteFormType[] = ["hair-analysis", "candidacy"];

/**
 * Persist accepted leads before client success, then notify the fixed owner.
 * Email failure never deletes a stored lead; persistence failure prevents success.
 * Sends contact info + form source only; health answers stay in the CRM.
 */
async function notifyClinicOfNewLead(input: WebsiteLeadInput, language: string): Promise<WebsiteLeadResult> {
  try {
    const callTime = input.details?.preferred_call_time;
    const { data, error } = await supabase.functions.invoke("notify-new-lead", {
      body: {
        name: (input.name || "").trim().slice(0, 100),
        phone: (input.phone || "").trim().slice(0, 30),
        email: (input.email || "").trim().toLowerCase() || undefined,
        source: SUBJECTS[input.formType], language,
        preferred_call_time: typeof callTime === "string" && callTime ? callTime.slice(0, 40) : undefined,
        has_health_data: HEALTH_FORMS.includes(input.formType), website: input.website || "",
      },
    });
    if (error || !data?.ok || !data?.id) return { ok: false, status: 502, error: "persistence_failed" };
    return { ok: true, duplicate: data.duplicate === true, data };
  } catch {
    return { ok: false, status: 502, error: "persistence_failed" };
  }
}

/** Localized failure message (form data is preserved by callers). */
export function crmBridgeErrorMessage(language: string): string {
  const map: Record<string, string> = {
    en: "We couldn't send your request. Please try again or reach us on WhatsApp: +31 6 38523131.",
    nl: "We konden uw aanvraag niet versturen. Probeer opnieuw of bereik ons via WhatsApp: +31 6 38523131.",
    fr: "Nous n'avons pas pu envoyer votre demande. Réessayez ou contactez-nous sur WhatsApp: +31 6 38523131.",
    de: "Ihre Anfrage konnte nicht gesendet werden. Bitte erneut versuchen oder WhatsApp: +31 6 38523131.",
    tr: "Talebiniz gönderilemedi. Lütfen tekrar deneyin veya WhatsApp: +31 6 38523131.",
    ar: "تعذر إرسال طلبك. يرجى المحاولة مرة أخرى أو التواصل عبر واتساب: +31 6 38523131.",
  };
  return map[(language || "en").toLowerCase()] || map.en;
}
